import { tt } from './i18n/translation-memory'

/**
 * Which order-status changes the server refuses on the ordinary `PATCH`, and who may make them -
 * `FRONTEND.md` §9.343.
 *
 * **Here rather than in a module because three separate callers need it.** §9.341 put this in
 * `manager-delivery/dispatch/board.ts` for the dispatch board; §9.342 found the board's *second*
 * path to `delivered`; and §9.343's sweep of every `PATCH /orders/{id}` caller found
 * `manager-orders/preorders` doing the same thing. **No module imports another module**, so the
 * alternative was copying four functions across - *one guard spelled twice*, on the very rule that
 * exists to stop that.
 *
 * Separate from `order-status.ts`, which is the **wording** (`ORDER_STATUS_LABELS`). This is the
 * rule about who may change what, and the two have no reason to share a file.
 */
/**
 * The statuses `OrderUpdateService` refuses on the ordinary `PATCH`.
 *
 * **Copied from the server's own `SETTLING` set**, and named for it rather than for the screen:
 *
 *     private static final Set<String> SETTLING = Set.of("delivered", "quality_check", "cancelled");
 *
 * The board sent `PATCH { status }` for all three and showed the server's refusal verbatim - *"Zmena
 * citlivého stavu vyžaduje overenie zamestnanca. Použite /confirm-status."* - which is an API
 * instruction printed to a dispatcher.
 */
export const SETTLING_STATUSES = ['delivered', 'quality_check', 'cancelled'] as const

/**
 * Who `PROTECTED_STATUS_ROLES` lets set each of them, mirrored from `OrderStatusService`.
 *
 * **`cancelled` is no longer here - §9.653, FB-983.** That transition is a **permission** on the server now
 * (`cancel_order`), because the owner's waiter holds it and both sides refused them by role: *"čašník have access
 * to storno objednávka, but he see error «Rola waiter nemá oprávnenie na túto akciu»"*. A copy of a role list is
 * what made the client agree with the defect, so the copy goes rather than gaining an entry.
 */
const ROLES_FOR: Record<string, readonly string[]> = {
  delivered: ['main_admin', 'admin', 'waiter', 'driver'],
  quality_check: ['main_admin', 'admin', 'cook'],
}

/** The right the server asks for a cancel - §9.653, `OrderStatusService.CANCEL_PERMISSION`. */
export const CANCEL_PERMISSION = 'cancel_order'

/**
 * Which endpoint this status change belongs to.
 *
 * **`confirm-status` is called with no credentials, deliberately.**
 * `OrderActorResolver.resolve(email, password)` falls back to `PrincipalContext.current()` when both
 * are absent, so the signed-in employee is the actor, the role gate still runs and `writeStatusLog`
 * still records who did it. A prompt asking a signed-in administrator for the password they signed in
 * with proves nothing the token has already proved - which is why this fix adds no modal.
 */
export function protectedStatusPlan(status: string): 'confirm' | 'patch' {
  return (SETTLING_STATUSES as readonly string[]).includes(status) ? 'confirm' : 'patch'
}

/**
 * Whether this person has to present credentials to make this change.
 *
 * **The owner's complaint, precisely.** `CancelOrderModal` asks for an e-mail and a password
 * unconditionally, so a `main_admin` is asked to retype the password they signed in with in order to
 * authorise something their own session already authorises. *"These popups are useless."*
 *
 * **And the credentials are not useless in general**, which is why this is a question rather than a
 * deletion: `cancelled` needs `{main_admin, admin}`, so on a till signed in as a **waiter** the box
 * is the *manager walks over and authorises it without logging the waiter out* flow -
 * `OrderActorResolver.byCredentials`. Removing it would take that away from every till that is not
 * an administrator's.
 *
 * So: may set it, the reason alone. May not, the reason and somebody who may.
 *
 * **A cancel asks the permission rather than the role - §9.653.** `hasPermission` is the reader `useAuth` already
 * exposes; pass it and a waiter who holds `cancel_order` is not asked for a password, which is the owner's
 * report. The box stays for somebody who does **not** hold it, because that is the *manager walks over and
 * authorises it without logging the waiter out* flow this function was written for.
 *
 * **An absent reader answers *no right*, so the box is shown.** Being asked for a password you do not need costs
 * a few seconds; skipping a prompt somebody does need lets a cancel through unattributed. That is the opposite
 * fallback direction from §9.651's, and for the opposite reason - there an empty answer would blank a room full
 * of guests.
 */
export function credentialsNeeded(
  systemRole: string,
  status: string,
  hasPermission?: (permission: string) => boolean,
): boolean {
  if (status === 'cancelled') return hasPermission?.(CANCEL_PERMISSION) !== true
  const roles = ROLES_FOR[status]
  if (roles === undefined) return false
  return !roles.includes(systemRole)
}

/** What each role is called on screen, so the note below reads as a sentence. */
function roleLabel(role: string): string {
  if (role === 'main_admin' || role === 'admin') return tt('administrátor')
  if (role === 'waiter') return tt('obsluha')
  if (role === 'driver') return tt('šofér')
  if (role === 'cook') return tt('kuchár')
  return role
}

/**
 * What to say when the server refuses the status for want of a role.
 *
 * Says **who may** rather than that somebody may not: a waiter who cannot storno needs to know it is
 * an administrator's action, and the server's own sentence names an endpoint instead.
 *
 * **Built from `ROLES_FOR` rather than written out per status**, so the mirrored table is
 * load-bearing: a role added or removed on the server changes this sentence when the table is
 * updated, instead of leaving a hand-written line that quietly disagrees with it. `main_admin` and
 * `admin` both read *administrátor*, so the list is de-duplicated after labelling.
 */
export function statusRoleNote(status: string): string {
  /* §9.653 - a cancel is a **right** rather than a job title, so the sentence names the right. Saying
     *administrátor* would send a waiter who has been granted `cancel_order` to find a manager, when what they
     need is for somebody to look at Nastavenia - Práva; and after this fix they are not refused at all. */
  if (status === 'cancelled') {
    return `${tt('Na stornovanie je potrebné právo')} „${tt('Stornovať objednávku')}".`
  }
  const roles = ROLES_FOR[status]
  if (roles === undefined) return tt('Tento stav sa nepodarilo nastaviť.')
  const who = [...new Set(roles.map(roleLabel))].join(', ')
  return `${tt('Tento stav môže nastaviť')}: ${who}.`
}

